<template>
  <header class="map-header" data-test-id="map-header">
    <div
      v-if="validMapCoordinates"
      class="map-header__images"
    >
      <a
        v-if="imageUrl && mainImage"
        :href="imageUrl"
        class="map-header__main-image-link"
        data-test-id="map-thumbnail-link"
        @click="onLinkClick"
      >
        <EThumbnail
          data-test-id="map-thumbnail"
          class="map-header__thumbnail"
          :src="mainImage.thumbnailUrl"
          :alt="mainImage.title"
        />
      </a>
      <EMapboxImage
        class="map-header__map"
        :marker-coordinates="[ { longitude: map.longitude, latitude: map.latitude } ]"
        :screen-sizes="screenSizes"
        :place-types="map.placeTypes"
        :location-name="map.placeName || title"
      >
        <EButton
          as="span"
          size="s"
          variant="solid-white"
          class="map-header__more"
          aria-hidden="true"
          data-test-id="map-header-more"
        >
          {{ $t('web.entities.openmap') }}
        </EButton>
      </EMapboxImage>
    </div>
    <ETitles
      :title="title"
      :subtitle="subtitle"
      class="map-header__title"
      data-test-id="entity-titles"
    />
  </header>
</template>
<script>
import { breakpoints } from '~~/common/js/constants/screen';
import EButton from '~~/common/vue/components/button';
import EThumbnail from '~~/common/vue/components/thumbnail';
import { trackClick, trackImpression, PAGE_SERP, TYPE_ENTITY } from '~~/search/common/js/client/analytics/rich-content';
import EMapboxImage from '~/components/mapbox-image';
import ETitles from './titles';

export const mapSizes = [
  '540x110',
  '970x110',
  '250x110',
];

export const TRACK_ID = 'map';

const { tablet, desktop } = breakpoints;
const screenSizes = [{ minWidth: 0 }, tablet, desktop].map((el, index) => {
  const imageSize = mapSizes[index];
  return { imageSize, minWidth: el.minWidth };
});

export default {
  name: 'EMapHeader',
  components: {
    EButton,
    EThumbnail,
    ETitles,
    EMapboxImage,
  },
  props: {
    title: ETitles.props.title,
    subtitle: ETitles.props.subtitle,
    imageUrl: {
      type: String,
      default: '',
    },
    image: {
      type: [Object, null],
      default: null,
    },
    map: {
      type: Object,
      required: true,
    },
    gallery: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    screenSizes() {
      return screenSizes;
    },
    mainImage() {
      if (this.image && this.image.thumbnailUrl) {
        return this.image;
      }

      const firstGalleryImage = this.gallery[0];
      return firstGalleryImage?.thumbnailUrl ? firstGalleryImage : null;
    },
    validMapCoordinates() {
      return this.map.longitude !== 0 && this.map.latitude !== 0;
    },
  },
  mounted() {
    trackImpression({
      name: TRACK_ID,
      page: PAGE_SERP,
      type: TYPE_ENTITY,
      once: true,
    });
  },
  methods: {
    onLinkClick: trackClick({
      name: TRACK_ID,
      type: TYPE_ENTITY,
      target: 'link',
    }),
  },
};
</script>

<style lang="scss" scoped>
.map-header__images {
  display: flex;
  position: relative;
  min-height: $entity-image-with-gallery-size;
}

.map-header__main-image-link {
  flex: 1;
  height: $entity-image-with-gallery-size;
}

.map-header__map {
  position: relative;
  flex: 2;
}

.map-header__more {
  position: absolute;
  bottom: $space-1s;
  right: $space-1s;
}

@include tablet {
  .web__mainline {
    .map-header__image {
      flex: 1;
      height: $entity-image-with-gallery-size;
    }

    .map-header__main-image {
      flex: 1;
    }
  }
}

.map-header__thumbnail {
  height: 100%;
}

.map-header__title {
  padding: $space-m $space-m 0;
}
</style>
